import { useCallback, useEffect } from "react";

import { useAssistantFeatureWalkthrough } from "../components/feature-walkthrough/hooks/useAssistantFeatureWalkthrough";
import { AssistantScenarioStatusEnum } from "../enums";
import {
  IUseAssistantInteractionsOptions,
  IUseAssistantInteractionsReturn,
} from "../types";
import { useAssistantWalkthrough } from "./useAssistantWalkthrough";

export const useAssistantInteractions = ({
  identity,
  page,
  onOpenConversation,
  onCloseConversation,
  onAsk,
  onAcknowledge,
  onScenarioCompleted,
  onCompleteGuide,
}: IUseAssistantInteractionsOptions): IUseAssistantInteractionsReturn => {
  const featureWalkthrough = useAssistantFeatureWalkthrough({
    identity,
    page,
    onAcknowledge,
    onScenarioCompleted,
  });
  const {
    feature,
    active: featureActive,
    handleResult: handleFeatureResult,
    handleClose: closeFeature,
    handleSave,
    handleFinish,
  } = featureWalkthrough;

  const handleComplete = useCallback(
    async (guideId: string, skipped: boolean) => {
      if (featureActive) {
        if (!feature) return false;
        return handleFeatureResult(guideId, skipped);
      } else if (!skipped) {
        return onCompleteGuide(guideId);
      }
    },
    [featureActive, feature, handleFeatureResult, onCompleteGuide]
  );

  const walkthrough = useAssistantWalkthrough(page, identity, handleComplete);
  const { guide, handleCancel, handleStart } = walkthrough;

  const handleClose = useCallback(() => {
    handleCancel();
    closeFeature();
    onCloseConversation();
  }, [handleCancel, closeFeature, onCloseConversation]);

  const handleDoneFeature = useCallback(() => {
    handleCancel();
    closeFeature();
    onOpenConversation();
  }, [handleCancel, closeFeature, onOpenConversation]);

  const handleAskQuestion = useCallback(
    (question?: string) => {
      onOpenConversation();
      handleCancel();
      closeFeature();
      void onAsk(question);
    },
    [onOpenConversation, handleCancel, closeFeature, onAsk]
  );

  const handleSelectScenario = useCallback(
    async (guideId: string) => {
      handleCancel();
      if (await handleSave(guideId, AssistantScenarioStatusEnum.IN_PROGRESS))
        handleStart(guideId);
    },
    [handleCancel, handleSave, handleStart]
  );

  const handleFinishFeature = useCallback(async () => {
    if (await handleFinish()) handleCancel();
  }, [handleFinish, handleCancel]);

  const handleSkipScenario = useCallback(async () => {
    if (!guide) return;
    if (await handleSave(guide.id, AssistantScenarioStatusEnum.SKIPPED))
      handleCancel();
  }, [handleSave, guide, handleCancel]);

  useEffect(() => {
    if (
      featureActive &&
      guide &&
      !feature?.scenarios.some((scenario) => scenario.guide.id === guide.id)
    )
      handleCancel();
  }, [featureActive, feature, guide, handleCancel]);

  return {
    featureWalkthrough,
    walkthrough,
    handleOpen: onOpenConversation,
    handleClose,
    handleAskQuestion,
    handleSelectScenario,
    handleFinishFeature,
    handleSkipScenario,
    handleDoneFeature,
  };
};
